import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Outlet, ScrollRestoration, useLocation } from "react-router-dom";
import { motion as Motion, useReducedMotion } from "framer-motion";
import i18n from "@/i18n";
import cls from "./HomeLayout.module.scss";

import homeIcon from "@/assets/UI/home.webp";
import menuIcon from "@/assets/UI/menu.webp";
import whoIcon from "@/assets/UI/who.webp";
import lawIcon from "@/assets/UI/law.webp";
import netWorkIcon from "@/assets/UI/notyNetwork.webp";
import loginIcon from "@/assets/UI/login.webp";
import citizenIcon from "@/assets/UI/citizen.webp";
import lanIcon from "@/assets/UI/lan.webp";
import fbIcon from "@/assets/UI/fb.webp";
import instagramIcon from "@/assets/UI/instagram.webp";
import youtubeIcon from "@/assets/UI/youtube.webp";
import tiktokIcon from "@/assets/UI/tiktok.webp";

const MENU_ID = "home-fullmenu";

const fadeIn = {
  hidden: { opacity: 0, y: -8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: "easeOut" },
  },
};

const ringClassMap = {
  pink: cls.ringPink,
  blue: cls.ringBlue,
  purple: cls.ringPurple,
  mint: cls.ringMint,
  gold: cls.ringGold,
  black: cls.ringBlack,
  red: cls.ringNotifRed,
};

const getRingClass = (ring = "pink") => ringClassMap[ring] ?? cls.ringBlack;

const quickActions = [
  {
    to: "/",
    ariaLabel: "The Portal",
    labelKey: "homeLayout.home",
    icon: homeIcon,
    ring: "pink",
    isActive: (pathname) => pathname === "/",
  },
  {
    to: "/terms",
    ariaLabel: "About us",
    labelKey: "homeLayout.aboutUs",
    icon: whoIcon,
    ring: "blue",
    isActive: (pathname) => pathname.startsWith("/terms"),
  },
  {
    to: "/noty-network",
    ariaLabel: "Noty Network",
    labelKey: "homeLayout.notyVlog",
    icon: netWorkIcon,
    ring: "purple",
    isActive: (pathname) => pathname.startsWith("/noty-network"),
  },
  {
    to: "/register",
    ariaLabel: "Register",
    labelKey: "homeLayout.register",
    icon: citizenIcon,
    ring: "mint",
    isActive: (pathname) => pathname.startsWith("/register"),
  },
  {
    to: "/login",
    ariaLabel: "Login",
    labelKey: "homeLayout.logIn",
    icon: loginIcon,
    ring: "gold",
    isActive: (pathname) => pathname.startsWith("/login"),
  },
];

const hasExternalHref = (href) =>
  typeof href === "string" && href.trim().length > 0;

const homeLayoutFallbacks = {
  drawerTitleLeft: "Noty Universe",
  drawerTitleRight: "Noty Social",
  home: "Noty Portal",
  language: "Pick your language",
  explore: "Explore Universe",
  terms: "The Noty Law Book",
  notyVlog: "Noty Network",
  register: "Apply for Noty Citizenship",
  logIn: "Enter the Universe",
  facebook: "Join our Facebook",
  instagram: "Check our Instagram",
  youtube: "Watch our YouTube",
  tiktok: "Follow us on TikTok",
  brandName: "Noty Universe",
  slogan: "Built for Free Thinkers.",
  aboutUs: "Wanna know who we are?",
  exploreUniverse: "About Noty Universe",
};

const tHomeLayout = (key) => {
  const legacyKey =
    key === "exploreUniverse" ? "home.exploreUniverse" : `homeLayout.${key}`;

  return i18n.t(`homeLayout:${key}`, {
    defaultValue: i18n.t(legacyKey, {
      defaultValue: homeLayoutFallbacks[key] ?? key,
    }),
  });
};

function QuickIcon({ src, ring = "pink", active = false, className = "" }) {
  return (
    <span
      className={`${cls.iconImgWrap} ${getRingClass(ring)} ${
        active ? cls.iconActive : ""
      } ${className}`.trim()}
      aria-hidden="true"
    >
      <img
        className={cls.iconImg}
        src={src}
        alt=""
        decoding="async"
        draggable="false"
      />
    </span>
  );
}

function MenuItem({ item, onNavigate }) {
  if (!item) return null;

  const content = (
    <>
      <img
        className={cls.menuCardIcon}
        src={item.icon}
        alt=""
        loading="lazy"
        decoding="async"
        draggable="false"
      />
      <span className={cls.menuCardLabel}>{item.label}</span>
      <span className={cls.menuCardChevron} aria-hidden="true">
        ›
      </span>
    </>
  );

  if (item.type === "external") {
    if (!hasExternalHref(item.href)) return null;

    return (
      <li>
        <a
          href={item.href}
          target="_blank"
          rel="noreferrer"
          onClick={onNavigate}
          className={cls.menuCard}
          aria-label={item.label}
        >
          {content}
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link
        to={item.to}
        onClick={onNavigate}
        className={cls.menuCard}
        aria-label={item.label}
      >
        {content}
      </Link>
    </li>
  );
}

export default function HomeLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const mainRef = useRef(null);
  const closeBtnRef = useRef(null);
  const { pathname, hash } = useLocation();
  const shouldReduceMotion = useReducedMotion();

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((value) => !value);
  }, []);

  useEffect(() => {
    if (hash) return;

    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  useEffect(() => {
    setMenuOpen((isOpen) => (isOpen ? false : isOpen));
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen || typeof document === "undefined") return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    requestAnimationFrame(() => closeBtnRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMenu, menuOpen]);

  const menuSections = useMemo(() => {
    const env = import.meta.env;

    return [
      {
        title: tHomeLayout("drawerTitleLeft"),
        items: [
          {
            type: "internal",
            to: "/",
            label: tHomeLayout("home"),
            icon: homeIcon,
          },
          {
            type: "internal",
            to: "/language",
            label: tHomeLayout("language"),
            icon: lanIcon,
          },
          {
            type: "internal",
            to: "/universe",
            label: tHomeLayout("explore"),
            icon: whoIcon,
          },
          {
            type: "internal",
            to: "/terms",
            label: tHomeLayout("terms"),
            icon: lawIcon,
          },
          {
            type: "internal",
            to: "/noty-network",
            label: tHomeLayout("notyVlog"),
            icon: netWorkIcon,
          },
          {
            type: "internal",
            to: "/register",
            label: tHomeLayout("register"),
            icon: citizenIcon,
          },
          {
            type: "internal",
            to: "/login",
            label: tHomeLayout("logIn"),
            icon: loginIcon,
          },
        ],
      },
      {
        title: tHomeLayout("drawerTitleRight"),
        items: [
          {
            type: "external",
            href: env.VITE_SOCIAL_FACEBOOK,
            label: tHomeLayout("facebook"),
            icon: fbIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_INSTAGRAM,
            label: tHomeLayout("instagram"),
            icon: instagramIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_YOUTUBE,
            label: tHomeLayout("youtube"),
            icon: youtubeIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_TIKTOK,
            label: tHomeLayout("tiktok"),
            icon: tiktokIcon,
          },
        ].filter((item) => hasExternalHref(item.href)),
      },
    ];
  }, []);

  const menuToggleMotionProps = shouldReduceMotion
    ? {}
    : { variants: fadeIn, initial: "hidden", animate: "show" };

  const menuIconOverlay = useMemo(() => {
    if (typeof document === "undefined") return null;

    return createPortal(
      <span className={cls.menuIconOverlay} aria-hidden="true">
        <span
          className={`${cls.iconImgWrap} ${cls.ringPurple} ${cls.quickIconUniform}`}
        >
          <img
            src={menuIcon}
            alt=""
            className={cls.iconImg}
            decoding="async"
            draggable="false"
          />
        </span>
      </span>,
      document.body,
    );
  }, []);

  return (
    <>
      <main className={cls.home}>
        <section className={`nu-shell ${cls.shell}`}>
          <div className={cls.stage}>
            <Motion.button
              type="button"
              className={`${cls.iconBtn} ${cls.menuToggle}`}
              {...menuToggleMotionProps}
              aria-expanded={menuOpen ? "true" : "false"}
              aria-controls={MENU_ID}
              onClick={toggleMenu}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            />

            {menuIconOverlay}

            <div className={cls.main} ref={mainRef}>
              <Outlet />
            </div>

            <nav className={cls.quickBar} aria-label="Quick actions">
              <ul className={cls.quickList}>
                {quickActions.map((action) => (
                  <li key={action.to}>
                    <Link
                      className={cls.roundBtn}
                      to={action.to}
                      aria-label={action.ariaLabel}
                    >
                      <QuickIcon
                        src={action.icon}
                        ring={action.ring}
                        active={action.isActive(pathname)}
                        className={cls.quickIconUniform}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </section>

        <div
          id={MENU_ID}
          className={`${cls.fullmenu} ${menuOpen ? cls["is-open"] : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Main Menu"
        >
          <button
            ref={closeBtnRef}
            type="button"
            className={`${cls.iconBtn} ${cls.menuClose}`}
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <i className="nu-icon nu-icon--close" aria-hidden="true" />
          </button>

          <button
            type="button"
            className={cls.fullmenuBackdrop}
            onClick={closeMenu}
            aria-label="Close menu overlay"
          />

          <div className={cls.fullmenuPanel}>
            <div className={cls.fullmenuInner}>
              <header className={cls.fullmenuHeader}>
                <p className={cls.fullmenuKicker}>{tHomeLayout("sloganTop")}</p>
                <h2 className={cls.fullmenuTitle}>
                  {tHomeLayout("brandName")}
                </h2>
                <p className={cls.fullmenuSubtitle}>{tHomeLayout("slogan")}</p>
              </header>

              <div className={cls.menuSectionGrid}>
                {menuSections.map((section) => (
                  <section className={cls.menuSection} key={section.title}>
                    <h3 className={cls.menuSectionTitle}>{section.title}</h3>

                    <ul className={cls.menuCardList}>
                      {section.items.map((item) => (
                        <MenuItem
                          key={`${item.type}-${item.to ?? item.href}`}
                          item={item}
                          onNavigate={closeMenu}
                        />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>

        {menuOpen && (
          <div className={cls.overlay} onClick={closeMenu} aria-hidden="true" />
        )}
      </main>

      <ScrollRestoration getKey={(loc) => loc.pathname} />
    </>
  );
}
